import { useEffect, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  bulkEditTargetIds,
  lineAfterToggle,
  lineIdsInShiftRange,
  pruneHighlightedIds,
  shownLineSelection,
  toggleLineId,
} from '../utils/assignmentLineHighlight';
import { assignmentLineHighlightSession } from '../utils/assignmentLineHighlightSession';
import { masterKeyIntent } from '../utils/masterKeyboard';
import { shouldOpenAssignmentLine } from './assignmentBoardRowClick';

function highlightSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

export type AssignmentLineHighlightOptions = {
  /**
   * When false, visible rows may be empty during a refetch (e.g. toggling
   * "Show trashed"). Skip pruning until loaded lines are shown again.
   */
  linesReady?: boolean;
  /** The line open in the Line section. */
  selectedLineId?: string;
  /** Close the Line section (Clear selection, or toggling its line out). */
  onClearLine?: () => void;
};

/** Ctrl-click (Windows/Linux) or Cmd-click (macOS) toggles one line. */
function isToggleClick(event: { ctrlKey: boolean; metaKey: boolean }) {
  return event.ctrlKey || event.metaKey;
}

export function useAssignmentLineHighlight(
  /** Stable per open cart (`supplierId:shipToWarehouseId`). */
  cartKey: string,
  /** Same order as the line table (grouped, filtered, sorted, visible rows only). */
  visibleLineIds: readonly string[],
  onSelectLine: (suggestionId: string) => void,
  options: AssignmentLineHighlightOptions = {},
) {
  const { linesReady = true, selectedLineId, onClearLine } = options;
  const visibleSet = new Set(visibleLineIds);
  const visibleKey = visibleLineIds.join('\0');
  const [trackedCartKey, setTrackedCartKey] = useState(cartKey);
  const [trackedVisibleKey, setTrackedVisibleKey] = useState(visibleKey);
  const [storedHighlightedIds, setStoredHighlightedIds] = useState<Set<string>>(
    () => {
      const sessionIds = assignmentLineHighlightSession(cartKey).highlightedIds;
      if (!linesReady) return new Set(sessionIds);
      return pruneHighlightedIds(sessionIds, new Set(visibleLineIds));
    },
  );

  if (cartKey !== trackedCartKey) {
    setTrackedCartKey(cartKey);
    setTrackedVisibleKey(visibleKey);
    const fromSession = assignmentLineHighlightSession(cartKey).highlightedIds;
    const pruned = linesReady
      ? pruneHighlightedIds(fromSession, visibleSet)
      : fromSession;
    assignmentLineHighlightSession(cartKey).highlightedIds = pruned;
    setStoredHighlightedIds(new Set(pruned));
  } else if (linesReady && visibleKey !== trackedVisibleKey) {
    setTrackedVisibleKey(visibleKey);
    const pruned = pruneHighlightedIds(storedHighlightedIds, visibleSet);
    if (!highlightSetsEqual(pruned, storedHighlightedIds)) {
      assignmentLineHighlightSession(cartKey).highlightedIds = pruned;
      setStoredHighlightedIds(new Set(pruned));
    }
  }

  const highlightedIds = storedHighlightedIds;

  const setHighlightedIds = (next: Set<string>) => {
    const pruned = pruneHighlightedIds(next, visibleSet);
    assignmentLineHighlightSession(cartKey).highlightedIds = pruned;
    setStoredHighlightedIds(new Set(pruned));
  };

  const editTargets = (originId: string) =>
    bulkEditTargetIds(highlightedIds, originId);

  const shown = shownLineSelection(highlightedIds, selectedLineId, visibleSet);

  const showLine = (next: Set<string>, toggledId: string) => {
    const line = lineAfterToggle(next, toggledId, selectedLineId);
    if (line === (selectedLineId ?? null)) return;
    if (line === null) onClearLine?.();
    else onSelectLine(line);
  };

  const handleLineRowClick = (
    line: AssignmentLine,
    event: ReactMouseEvent<HTMLTableRowElement>,
  ) => {
    if (!shouldOpenAssignmentLine(event.target)) return;
    const session = assignmentLineHighlightSession(cartKey);
    const id = line.suggestionId;
    if (event.shiftKey && session.anchorId != null) {
      const range = lineIdsInShiftRange(visibleLineIds, session.anchorId, id);
      if (range.length > 0) {
        // Ctrl/Cmd+Shift adds the range to the lines already selected.
        setHighlightedIds(
          new Set(isToggleClick(event) ? [...shown, ...range] : range),
        );
        return;
      }
    }

    session.anchorId = id;
    if (isToggleClick(event)) {
      const next = toggleLineId(shown, id);
      setHighlightedIds(next);
      showLine(next, id);
      return;
    }
    if (!highlightedIds.has(id) || highlightedIds.size <= 1) {
      setHighlightedIds(new Set([id]));
    }
    onSelectLine(id);
  };

  /**
   * Select only this line, as stepping with ↑/↓ does: a multi-selection
   * collapses to the line the footer moves to (GEN-3285 Q5).
   */
  const selectOnly = (suggestionId: string) => {
    assignmentLineHighlightSession(cartKey).anchorId = suggestionId;
    setHighlightedIds(new Set([suggestionId]));
  };

  /** Drop every selected line and close the Line section. */
  const clearSelection = () => {
    assignmentLineHighlightSession(cartKey).anchorId = null;
    setHighlightedIds(new Set());
    if (selectedLineId !== undefined) onClearLine?.();
  };

  // The open line keeps its own selected look (getRowAccentClass).
  const isBulkHighlighted = (suggestionId: string) => shown.has(suggestionId);

  return {
    highlightedIds,
    editTargets,
    handleLineRowClick,
    isBulkHighlighted,
    selectedCount: shown.size,
    clearSelection,
    selectOnly,
  };
}

/**
 * Esc clears a multi-line selection before the page's own Esc (which closes
 * the Line section, then the cart) sees the key. Capture phase, so it runs
 * ahead of the page's window listener whatever order they were added in.
 */
export function useClearSelectionKey(
  multiSelected: boolean,
  onClear: () => void,
  disabled = false,
): void {
  useEffect(() => {
    if (disabled || !multiSelected) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (masterKeyIntent(event) !== 'close') return;
      event.preventDefault();
      onClear();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [disabled, multiSelected, onClear]);
}
